import { StageInfo, StageGroup, GroupedStage } from '../types/stageTypes';
import { DefaultStageMappingType } from '../../../api/integrations';

/**
 * Applies a stage type to selected stages
 * @param stageGroups Current stage groups
 * @param selectedStages Set of selected stage IDs
 * @param stageType Target stage type
 * @param isUnmapped Whether to mark as unmapped
 * @returns Updated stage info with applied changes
 */
export const applyTypeToStages = (
    stageGroups: StageGroup[],
    originalStages: StageInfo[],
    selectedStages: Set<string>,
    stageType: DefaultStageMappingType | null,
    isUnmapped: boolean = false
): StageInfo[] => {
    if (selectedStages.size === 0) return originalStages;

    // Map to keep track of grouped stages by ID
    const groupedStagesMap = new Map<string, {
        stage: GroupedStage,
        group: StageGroup
    }>();

    // Populate the map
    stageGroups.forEach(group => {
        group.stages.forEach(stage => {
            groupedStagesMap.set(stage.id, { stage, group });
        });
    });

    // Create new allStages array with updated mappings
    const updatedStages = originalStages.map(stage => {
        // Check if this stage is in our selection
        const isSelected = selectedStages.has(stage.id);
        if (!isSelected) {
            return stage; // Return unchanged
        }

        // Start with the original mapping
        const newMapping = { ...stage.mapping };

        // Apply the target stage type and unmapped state
        newMapping.stage_type = stageType;
        newMapping.is_unmapped = isUnmapped;

        // If it's an interview stage, we need to set an order
        if (stageType === 'INTERVIEW' && !isUnmapped) {
            // Try to preserve existing order if it was already an interview stage
            if (stage.mapping.stage_type === 'INTERVIEW') {
                // Keep existing order
            } else {
                // Set to a high order to place it at the end
                const maxInterviewOrder = Math.max(
                    ...originalStages
                        .filter(s => s.mapping.stage_type === 'INTERVIEW')
                        .map(s => s.mapping.order || 0),
                    -1 // Default if no interview stages exist
                );
                newMapping.order = maxInterviewOrder + 1;
            }
        }

        return {
            ...stage,
            mapping: newMapping,
            isChanged: true
        };
    });

    return updatedStages;
};

/**
 * Toggles the "always unmap" state for a specific stage
 * @param stageGroups Current stage groups
 * @param originalStages Original stage data
 * @param stageId ID of the stage to toggle
 * @param unmapped New unmapped state
 * @returns Updated stage info with the toggled state
 */
export const toggleStageUnmappedState = (
    stageGroups: StageGroup[],
    originalStages: StageInfo[],
    stageId: string,
    unmapped: boolean
): StageInfo[] => {
    // First find the stage in the grouped stages
    let targetGroupedStage: GroupedStage | undefined;
    let targetGroup: StageGroup | undefined;

    for (const group of stageGroups) {
        const foundStage = group.stages.find(stage => stage.id === stageId);
        if (foundStage) {
            targetGroupedStage = foundStage;
            targetGroup = group;
            break;
        }
    }

    if (!targetGroupedStage || !targetGroup) {
        console.error('Could not find stage to toggle', stageId);
        return originalStages;
    }

    // Get the original stage
    const originalStage = originalStages.find(stage => stage.id === targetGroupedStage?.originalId);
    if (!originalStage) {
        console.error('Could not find original stage', stageId);
        return originalStages;
    }

    // Update the stage mapping
    return originalStages.map(stage => {
        if (stage.id === targetGroupedStage?.originalId) {
            return {
                ...stage,
                mapping: {
                    ...stage.mapping,
                    is_unmapped: unmapped,
                    // If turning on unmapped, remove the stage type
                    stage_type: unmapped ? null : stage.mapping.stage_type
                },
                isChanged: true
            };
        }
        return stage;
    });
};

/**
 * Calculates change counts for confirmation dialog
 * @param stageGroups Current stage groups
 * @returns Object with counts of added, updated, and unmapped stages
 */
export const calculateChangeCounts = (stageGroups: StageGroup[]) => {
    let added = 0;
    let updated = 0;
    let unmapped = 0;

    stageGroups.forEach(group => {
        group.stages.forEach(stage => {
            if (stage.isNew && stage.mapping.stage_type) {
                added++;
            } else if (stage.isChanged) {
                if (stage.mapping.is_unmapped) {
                    unmapped++;
                } else {
                    updated++;
                }
            }
        });
    });

    return { addedCount: added, updatedCount: updated, unmappedCount: unmapped };
}; 